import {getTestProps} from '@benchling/js-test-utils';
import {Group} from '@visx/group';
import {Circle} from '@visx/shape';
import React from 'react';

import Cross from 'ui-library/charting/internals/Cross';
import {dataAccessorForColumnIfPresent} from 'ui-library/charting/internals/utils/dataAccessor';
import {
  EXCLUDED_OUTLIER_SCATTER_PLOT_POINT_COLOR,
  MASKED_SCATTER_PLOT_POINT_COLOR,
  OUTLIER_SCATTER_PLOT_POINT_COLOR,
  POINT_RADIUS,
} from 'ui-library/charting/internals/utils/Theme';
import {AxisTypes, CartesianPoint as CartesianPointType} from 'ui-library/charting/Types';

import {CartesianPointHoverInfo} from './CartesianPoints';

export const EXCLUDED_OUTLIER_TRANSPARENCY = 0.5;
export const HOVER_RADIUS_MULTIPLIER = 1.5;

// Be careful about adding any new props that could break the memo-ization, as that would cause all points to
// re-render when one is hovered/unhovered.
export interface CartesianPointProps<DataPoint extends CartesianPointType> {
  axisTypes: AxisTypes;
  color: string;
  isHovered: boolean;
  dataPoint: DataPoint;
  dataPointKey: string;
  onClick?: (dataPoint: DataPoint) => void;
  onHover: (info: CartesianPointHoverInfo<DataPoint> | null) => void;
  x: number;
  y: number;
}

function CartesianPoint<DataPoint extends CartesianPointType>({
  axisTypes,
  color,
  isHovered,
  dataPoint,
  dataPointKey,
  onClick,
  onHover,
  x,
  y,
}: CartesianPointProps<DataPoint>): JSX.Element {
  const isMaskedAccessor = dataAccessorForColumnIfPresent<CartesianPointType, 'isMasked'>(
    'isMasked',
    axisTypes.isMasked
  );

  const isOutlierAccessor = dataAccessorForColumnIfPresent<CartesianPointType, 'isOutlier'>(
    'isOutlier',
    axisTypes.isOutlier
  );

  const isMasked = (isMaskedAccessor != null && isMaskedAccessor(dataPoint)) as boolean;
  const isOutlier = (isOutlierAccessor != null && isOutlierAccessor(dataPoint)) as boolean;

  return (
    <Group
      left={x}
      pointerEvents="all"
      {...getTestProps({component: 'CartesianPoint', element: 'root'})}
      onClick={() => {
        if (onClick != null) {
          onClick(dataPoint);
        }
      }}
      onMouseLeave={() => {
        onHover(null);
      }}
      onMouseOver={() => {
        onHover({dataPoint, dataPointKey, x, y});
      }}
      top={y}
    >
      <Circle // Larger transparent point used to make hovering easier
        fill="none"
        r={HOVER_RADIUS_MULTIPLIER * POINT_RADIUS}
        {...getTestProps({component: 'CartesianPoint', element: 'hoverPoint'})}
      />
      {isOutlier ? (
        <Cross
          {...{
            color: isMasked ? EXCLUDED_OUTLIER_SCATTER_PLOT_POINT_COLOR : OUTLIER_SCATTER_PLOT_POINT_COLOR,
            radius: isHovered ? HOVER_RADIUS_MULTIPLIER * POINT_RADIUS : POINT_RADIUS,
            x: 0,
            y: 0,
          }}
        />
      ) : (
        <Circle // Displayed point
          fill={isMasked ? MASKED_SCATTER_PLOT_POINT_COLOR : color}
          r={isHovered ? HOVER_RADIUS_MULTIPLIER * POINT_RADIUS : POINT_RADIUS}
          {...getTestProps({component: 'CartesianPoint', element: 'visiblePoint'})}
        />
      )}
    </Group>
  );
}

export default React.memo(CartesianPoint);
